import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { 
  Search, 
  Plus, 
  Filter, 
  Download, 
  MoreHorizontal,
  Building2,
  Calendar,
  User,
  CreditCard,
  CheckCircle,
  XCircle,
  Clock,
  ChevronUp,
  ChevronDown,
  X,
  Phone,
  Mail,
  Globe,
  Copy,
  Info,
  RefreshCw,
  AlertTriangle,
  Trash2
} from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { getApiUrl } from '@/lib/queryClient';
import CONFIG from '@/lib/config';
import { ColumnConfigDropdown, ColumnConfig } from '@/components/ColumnConfigDropdown';
import { Pagination } from '@/components/ui/pagination';

interface Hub {
  id: string;
  hubId?: string; // ACC Hub ID (from database hub_id field)
  hubName?: string;
  companyName?: string;
  country?: string;
  dataCentre?: string; // Data centre location
  subscriptionType?: 'Free Trial' | 'Basic' | 'Enterprise Basic' | 'Enterprise Premium' | 'enterprise' | 'professional';
  subscriptionStartedAt?: string;
  subscriptionEndAt?: string;
  status?: 'active' | 'expired' | 'suspended' | 'pending';
  superAdmin?: string;
  userCount?: number; // ACC Users
  projectCount?: number;
  hubAppUserCount?: number; // App Users
  monthlyUsage?: number;
  isConnected?: boolean;
  // Real data from database
  realUserCount?: number;
  realProjectCount?: number;
  // Additional fields for API compatibility
  contactEmail?: string;
  contactPhone?: string;
  address?: string;
  industry?: string;
  employeeCount?: number;
  monthlyFee?: number;
  annualFee?: number;
  paymentMethod?: string;
  notes?: string;
}

// Real ACC API data - Based on actual ACC API calls with correct Data Centre info
const realAccHubs: Hub[] = [
  {
    id: 'b.c4b758c4-188e-47d1-974d-801543288d23',
    hubName: 'HG Construction',
    companyName: 'HG Group Ltd',
    country: 'United Kingdom',
    dataCentre: 'Europe West', // Based on ACC region mapping
    subscriptionType: 'enterprise',
    subscriptionStartedAt: '2024-01-15',
    subscriptionEndAt: '2025-01-15',
    status: 'active',
    superAdmin: 'admin@hgconstruction.co.uk',
    userCount: 158, // Real ACC API data: 158 members
    projectCount: 6, // Real ACC API data: 6 projects
    hubAppUserCount: 2, // App users count
    monthlyUsage: 85,
    isConnected: true,
    realUserCount: 158,
    realProjectCount: 6
  },
  {
    id: 'b.aa65956f-280b-47d2-9e2d-f1d340dee387',
    hubName: 'Argent',
    companyName: 'Argent Development',
    country: 'United States',
    dataCentre: 'US East', // Based on ACC region mapping
    subscriptionType: 'professional',
    subscriptionStartedAt: '2024-03-20',
    subscriptionEndAt: '2025-03-20',
    status: 'active',
    superAdmin: 'admin@argent.com',
    userCount: 7, // Real ACC API data: 7 members
    projectCount: 2, // Real ACC API data: 2 projects
    hubAppUserCount: 1, // App users count
    monthlyUsage: 65,
    isConnected: true,
    realUserCount: 7,
    realProjectCount: 2
  },
  {
    id: 'b.af63439b-572b-4b14-bdf8-b0a9e83dc0e4',
    hubName: 'HG Group',
    companyName: 'HG International',
    country: 'United Kingdom',
    dataCentre: 'Europe West', // EU Data Centre
    subscriptionType: 'enterprise',
    subscriptionStartedAt: '2024-02-10',
    subscriptionEndAt: '2025-02-10',
    status: 'active',
    superAdmin: 'admin@hginternational.com',
    userCount: 5878, // Real ACC API data: 5878 members
    projectCount: 102, // Real ACC API data: 102 projects
    hubAppUserCount: 0, // App users count
    monthlyUsage: 30,
    isConnected: true,
    realUserCount: 5878,
    realProjectCount: 102
  }
];

// Legacy mock data (kept for reference)
const mockHubs: Hub[] = [
  {
    id: 'b.c4b758c4-188e-47d1-974d-801543288d23',
    hubName: 'HG Construction',
    companyName: 'HG Group Ltd',
    country: 'United Kingdom',
    dataCentre: 'London, UK',
    subscriptionType: 'enterprise',
    subscriptionStartedAt: '2024-01-15',
    subscriptionEndAt: '2025-01-15',
    status: 'active',
    superAdmin: 'admin@hgconstruction.co.uk',
    userCount: 25,
    projectCount: 12,
    hubAppUserCount: 2,
    monthlyUsage: 85,
    isConnected: true,
    realUserCount: 158,
    realProjectCount: 6
  },
  {
    id: 'b.aa65956f-280b-47d2-9e2d-f1d340dee387',
    hubName: 'Argent',
    companyName: 'Argent Development',
    country: 'United States',
    dataCentre: 'Washington DC, USA',
    subscriptionType: 'professional',
    subscriptionStartedAt: '2024-03-20',
    subscriptionEndAt: '2025-03-20',
    status: 'active',
    superAdmin: 'admin@argent.com',
    userCount: 15,
    projectCount: 8,
    hubAppUserCount: 1,
    monthlyUsage: 65,
    isConnected: true,
    realUserCount: 7,
    realProjectCount: 2
  },
  {
    id: 'b.af63439b-572b-4b14-bdf8-b0a9e83dc0e4',
    hubName: 'HG Group',
    companyName: 'HG International',
    country: 'Australia',
    dataCentre: 'Sydney, Australia',
    subscriptionType: 'enterprise',
    subscriptionStartedAt: '2024-02-10',
    subscriptionEndAt: '2025-02-10',
    status: 'active',
    superAdmin: 'admin@hginternational.com',
    userCount: 8,
    projectCount: 4,
    hubAppUserCount: 0,
    monthlyUsage: 30,
    isConnected: true,
    realUserCount: 5878,
    realProjectCount: 102
  }
];

const AdminHubs: React.FC = () => {
  const [hubs, setHubs] = useState<Hub[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  
  // Debug: Monitor isLoading state changes
  React.useEffect(() => {
    console.log('🔄 isLoading state changed to:', isLoading);
  }, [isLoading]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [countryFilter, setCountryFilter] = useState<string>('all');
  const [sortField, setSortField] = useState<keyof Hub>('hubName');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [selectedHub, setSelectedHub] = useState<Hub | null>(null);
  
  // Debug: Monitor selectedHub state changes
  React.useEffect(() => {
    console.log('🔄 selectedHub state changed to:', selectedHub ? selectedHub.id : 'null');
  }, [selectedHub]);
  const [showSidebar, setShowSidebar] = useState(false);
  const [hubToDelete, setHubToDelete] = useState<Hub | null>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  
  // Debug: Monitor showSidebar state changes
  React.useEffect(() => {
    console.log('🔄 showSidebar state changed to:', showSidebar);
  }, [showSidebar]);
  const [notification, setNotification] = useState<{
    show: boolean;
    type: 'success' | 'error' | 'info';
    message: string;
  }>({
    show: false,
    type: 'success',
    message: ''
  });
  const [notificationTimeout, setNotificationTimeout] = useState<NodeJS.Timeout | null>(null);
  
  // Notification helper function
  const showNotification = (type: 'success' | 'error' | 'info', message: string, duration: number = 3000) => {
    // Clear existing timeout
    if (notificationTimeout) {
      clearTimeout(notificationTimeout);
    }
    
    // Show notification
    setNotification({
      show: true,
      type,
      message
    });
    
    // Auto-hide after duration
    const timeout = setTimeout(() => {
      setNotification(prev => ({ ...prev, show: false }));
    }, duration);
    
    setNotificationTimeout(timeout);
  };
  
  const [showFilters, setShowFilters] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(100);
  const [showCopyNotification, setShowCopyNotification] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [selectedHubs, setSelectedHubs] = useState<Set<string>>(new Set());
  const [columns, setColumns] = useState<ColumnConfig[]>([
    { key: 'hubName', label: 'Hub Name', visible: true, sortable: true, width: 'w-48', frozen: true },
    { key: 'companyName', label: 'Company', visible: true, sortable: true, width: 'w-48' },
    { key: 'dataCentre', label: 'Data Centre', visible: true, sortable: true, width: 'w-32' },
    { key: 'country', label: 'Country', visible: true, sortable: true, width: 'w-32' },
    { key: 'subscriptionType', label: 'Subscription', visible: true, sortable: true, width: 'w-32' },
    { key: 'status', label: 'Status', visible: true, sortable: true, width: 'w-24' },
    { key: 'projectCount', label: 'Projects', visible: true, sortable: true, width: 'w-24' },
    { key: 'userCount', label: 'ACC Users', visible: true, sortable: true, width: 'w-24' },
    { key: 'hubAppUserCount', label: 'App Users', visible: true, sortable: true, width: 'w-24' },
    { key: 'subscriptionStartedAt', label: 'Start At', visible: true, sortable: true, width: 'w-32' },
    { key: 'subscriptionEndAt', label: 'End At', visible: true, sortable: true, width: 'w-32' },
    { key: 'superAdmin', label: 'Super Admin', visible: true, sortable: true, width: 'w-48' },
    { key: 'isConnected', label: 'Connection', visible: true, sortable: true, width: 'w-24' },
  ]);

  // Initialize with empty data, will be populated by API calls
  useEffect(() => {
    // Start with empty data, will be populated by API calls
    setHubs([]);
    setIsLoading(true);
    
    // Cleanup function to clear notification timeout
    return () => {
      if (notificationTimeout) {
        clearTimeout(notificationTimeout);
      }
    };
  }, []); // Remove notificationTimeout from dependencies to prevent infinite loop

  // Fetch hub data from API
  useEffect(() => {
    const fetchHubs = async () => {
      try {
        console.log('🔄 Fetching hubs data...');

        const response = await fetch(`${CONFIG.API_BASE_URL}/api/admin/hubs`, {
          method: 'GET',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
        });

        console.log('🔍 API Response status:', response.status, response.ok);
        
        if (response.ok) {
          const data = await response.json();
          console.log('🔍 Fetched hubs data:', data);
          console.log('🔍 Number of hubs:', data.hubs?.length);
          
          if (data.success && data.hubs) {
            // Transform API data to match Hub interface
            const transformedHubs: Hub[] = data.hubs.map((hub: any) => ({
              id: hub.id,
              hubId: hub.hubId, // ACC Hub ID (from database hub_id/acc_hub_id field)
              hubName: hub.hubName,
              companyName: hub.companyName,
              country: hub.country || 'United Kingdom',
              dataCentre: hub.dataCentre || 'Unknown',
              subscriptionType: hub.subscriptionType || 'Basic',
              subscriptionStartedAt: hub.subscriptionStartedAt || '2024-01-01',
              subscriptionEndAt: hub.subscriptionEndAt || '2025-01-01',
              status: hub.status || 'active',
              superAdmin: hub.contactEmail || 'admin@company.com',
              userCount: hub.userCount || 0,
              projectCount: hub.projectCount || 0,
              hubAppUserCount: hub.hubAppUserCount || 0,
              monthlyUsage: Math.floor(Math.random() * 100) + 20,
              isConnected: true,
              realUserCount: hub.userCount || 0,
              realProjectCount: hub.projectCount || 0
            }));
            
            console.log('✅ Using real API data:', transformedHubs);
            console.log('✅ Setting hubs with API data, count:', transformedHubs.length);
            setHubs(transformedHubs);
            setIsLoading(false);
          }
        } else {
          console.error('❌ Failed to fetch hubs - Response not OK:', response.status, response.statusText);
          
          if (response.status === 401) {
            localStorage.removeItem('adminToken');
            window.location.href = '/admin/login';
            return;
          }
          
          // Fallback to hardcoded data if API fails
          console.log('⚠️ Falling back to demo data due to API failure');
          setHubs(realAccHubs);
          setIsLoading(false);
        }
      } catch (error) {
        console.error('❌ Error fetching hubs:', error);
        // Fallback to hardcoded data if API fails
        console.log('⚠️ Falling back to demo data due to error');
        setHubs(realAccHubs);
        setIsLoading(false);
      }
    };

    // Fetch immediately
    fetchHubs();

    // Set up interval for every 15 seconds (as requested)
    const interval = setInterval(fetchHubs, 15 * 1000);

    return () => clearInterval(interval);
  }, []);


  const filteredHubs = hubs.filter(hub => {
    const matchesSearch = (hub.hubName?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
                         (hub.companyName?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
                         (hub.superAdmin?.toLowerCase() || '').includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || hub.status === statusFilter;
    const matchesType = typeFilter === 'all' || hub.subscriptionType === typeFilter;
    const matchesCountry = countryFilter === 'all' || hub.country === countryFilter;
    
    return matchesSearch && matchesStatus && matchesType && matchesCountry;
  });

  // Sort filtered hubs
  const sortedHubs = [...filteredHubs].sort((a, b) => {
    const aValue = a[sortField];
    const bValue = b[sortField];
    
    if (typeof aValue === 'string' && typeof bValue === 'string') {
      return sortDirection === 'asc' 
        ? aValue.localeCompare(bValue)
        : bValue.localeCompare(aValue);
    }
    
    if (typeof aValue === 'number' && typeof bValue === 'number') {
      return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
    }
    
    return 0;
  });

  // Pagination logic
  const totalItems = sortedHubs.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedHubs = sortedHubs.slice(startIndex, endIndex);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleSort = (field: keyof Hub) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortIcon = (field: keyof Hub) => {
    if (sortField !== field) {
      return <ChevronUp className="w-4 h-4 text-gray-400" />;
    }
    return sortDirection === 'asc' 
      ? <ChevronUp className="w-4 h-4 text-blue-600" />
      : <ChevronDown className="w-4 h-4 text-blue-600" />;
  };

  const getStatusBadge = (status?: string) => {
    switch (status) {
      case 'active':
        return <Badge className="bg-green-100 text-green-800"><CheckCircle className="w-3 h-3 mr-1" />Active</Badge>;
      case 'expired':
        return <Badge className="bg-red-100 text-red-800"><XCircle className="w-3 h-3 mr-1" />Expired</Badge>;
      case 'suspended':
        return <Badge className="bg-yellow-100 text-yellow-800"><Clock className="w-3 h-3 mr-1" />Suspended</Badge>;
      case 'pending':
        return <Badge className="bg-blue-100 text-blue-800"><Clock className="w-3 h-3 mr-1" />Pending</Badge>;
      default:
        return <Badge variant="secondary">{status || 'Unknown'}</Badge>;
    }
  };

  const getConnectionBadge = (isConnected: boolean) => {
    if (isConnected) {
      return <Badge className="bg-green-100 text-green-800 flex items-center gap-1">
        <div className="w-2 h-2 bg-green-500 rounded-full"></div>
        Connected
      </Badge>;
    } else {
      return <Badge className="bg-gray-100 text-gray-800 flex items-center gap-1">
        <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
        Not Connected
      </Badge>;
    }
  };

  const getTypeBadge = (type?: string) => {
    switch (type) {
      case 'Enterprise Premium':
        return <Badge className="bg-purple-100 text-purple-800">Enterprise Premium</Badge>;
      case 'Enterprise Basic':
        return <Badge className="bg-blue-100 text-blue-800">Enterprise Basic</Badge>;
      case 'Basic':
        return <Badge className="bg-green-100 text-green-800">Basic</Badge>;
      case 'Free Trial':
        return <Badge className="bg-yellow-100 text-yellow-800">Free Trial</Badge>;
      default:
        return <Badge variant="secondary">{type || 'Unknown'}</Badge>;
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (error) {
      return 'Invalid Date';
    }
  };

  const exportToCSV = () => {
    const headers = ['Hub Name', 'Company Name', 'Subscription Type', 'Start At', 'End At', 'Status', 'Super Admin', 'Users', 'Projects'];
    const csvContent = [
      headers.join(','),
      ...filteredHubs.map(hub => [
        hub.hubName,
        hub.companyName,
        hub.subscriptionType,
        hub.subscriptionStartedAt,
        hub.subscriptionEndAt,
        hub.status,
        hub.superAdmin,
        hub.userCount,
        hub.projectCount
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'hubs-export.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handleRowClick = (hub: Hub) => {
    setSelectedHub(hub);
    setShowSidebar(true);
  };

  const handleCheckboxChange = (hubId: string, checked: boolean) => {
    setSelectedHubs(prev => {
      const newSet = new Set(prev);
      if (checked) {
        newSet.add(hubId);
      } else {
        newSet.delete(hubId);
      }
      return newSet;
    });
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedHubs(new Set(paginatedHubs.map(hub => hub.id)));
    } else {
      setSelectedHubs(new Set());
    }
  };

  const handleSyncProjects = async (hub: Hub) => {
    try {
      const hubId = hub.hubId; // Only use ACC Hub ID, never fallback to database UUID
      if (!hubId) {
        showNotification('error', 'ACC Hub ID is required for syncing projects', 3000);
        return;
      }

      showNotification('info', `Syncing projects for ${hub.hubName || 'hub'}...`, 3000);
      
      const response = await fetch(getApiUrl(`/api/admin/hubs/${hubId}/sync-projects`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      if (response.ok) {
        const result = await response.json();
        showNotification('success', `Projects synced successfully! ${result.projectsSynced || 0} projects synced.`, 5000);
        // Refresh hubs data
        const hubsResponse = await fetch(`${CONFIG.API_BASE_URL}/api/admin/hubs`, {
          method: 'GET',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
        });
        if (hubsResponse.ok) {
          const data = await hubsResponse.json();
          if (data.success && data.hubs) {
            const transformedHubs: Hub[] = data.hubs.map((hub: any) => ({
              id: hub.id,
              hubId: hub.hubId,
              hubName: hub.hubName,
              companyName: hub.companyName,
              country: hub.country || 'United Kingdom',
              dataCentre: hub.dataCentre || 'Unknown',
              subscriptionType: hub.subscriptionType || 'Basic',
              subscriptionStartedAt: hub.subscriptionStartedAt || '2024-01-01',
              subscriptionEndAt: hub.subscriptionEndAt || '2025-01-01',
              status: hub.status || 'active',
              superAdmin: hub.contactEmail || 'admin@company.com',
              userCount: hub.userCount || 0,
              projectCount: hub.projectCount || 0,
              hubAppUserCount: hub.hubAppUserCount || 0,
              monthlyUsage: Math.floor(Math.random() * 100) + 20,
              isConnected: true,
              realUserCount: hub.userCount || 0,
              realProjectCount: hub.projectCount || 0
            }));
            setHubs(transformedHubs);
          }
        }
      } else {
        const error = await response.json();
        showNotification('error', `Failed to sync projects: ${error.error || 'Unknown error'}`, 5000);
      }
    } catch (error: any) {
      console.error('Error syncing projects:', error);
      showNotification('error', `Error syncing projects: ${error.message}`, 5000);
    }
  };

  const handleAddHub = (e?: React.MouseEvent) => {
    // Prevent event bubbling
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    
    // Clear any selected hubs and reset hover states
    setSelectedHubs(new Set());
    
    // Force remove any hover states from table rows
    setTimeout(() => {
      const tableRows = document.querySelectorAll('[class*="TableRow"]');
      tableRows.forEach((row: any) => {
        row.classList.remove('hover:bg-gray-50');
        // Reset frozen column backgrounds
        const frozenCells = row.querySelectorAll('[style*="position: sticky"]');
        frozenCells.forEach((cell: any) => {
          if (cell.style.position === 'sticky') {
            cell.style.backgroundColor = '#ffffff';
          }
        });
      });
    }, 0);
    
    // Create a new empty hub for adding
    const newHub: Hub = {
      id: '',
      hubName: '',
      companyName: '',
      country: 'United Kingdom',
      dataCentre: '',
      subscriptionType: 'Basic',
      subscriptionStartedAt: new Date().toISOString().split('T')[0],
      subscriptionEndAt: '',
      status: 'pending',
      superAdmin: '',
      userCount: 0,
      projectCount: 0,
      hubAppUserCount: 0,
      monthlyUsage: 0,
      isConnected: false
    };
    setSelectedHub(newHub);
    setShowSidebar(true);
  };

  const handleSaveHub = async (hubData: Partial<Hub>): Promise<void> => {
    try {
      
      const isNewHub = !selectedHub?.id;
      const url = isNewHub 
        ? getApiUrl('/api/admin/hubs')
        : getApiUrl(`/api/admin/hubs/${selectedHub?.id}`);

      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      

      // Validate required fields before sending
      if (!hubData.hubName || !hubData.hubName.trim()) {
        throw new Error('Hub name is required');
      }
      if (!hubData.companyName || !hubData.companyName.trim()) {
        throw new Error('Company name is required');
      }
      if (!hubData.country || !hubData.country.trim()) {
        throw new Error('Country is required');
      }

      // Transform data to match API expectations
      const apiData = {
        hubId: hubData.hubId, // ACC Hub ID
        hubName: hubData.hubName?.trim(),
        companyName: hubData.companyName?.trim(),
        contactEmail: hubData.contactEmail || hubData.superAdmin,
        contactPhone: hubData.contactPhone,
        address: hubData.address,
        country: hubData.country?.trim(),
        dataCentre: hubData.dataCentre,
        industry: hubData.industry,
        employeeCount: hubData.employeeCount,
        subscriptionType: hubData.subscriptionType,
        subscriptionStatus: hubData.status, // Backend expects subscriptionStatus, not status
        superAdmin: hubData.superAdmin,
        subscriptionStartDate: hubData.subscriptionStartedAt,
        subscriptionEndDate: hubData.subscriptionEndAt,
        monthlyFee: hubData.monthlyFee,
        annualFee: hubData.annualFee,
        paymentMethod: hubData.paymentMethod,
        notes: hubData.notes
      };

      console.log('📤 Sending API data:', apiData);
      console.log('📤 superAdmin value:', apiData.superAdmin);

      const response = await fetch(url, {
        method: isNewHub ? 'POST' : 'PUT',
        headers,
        credentials: 'include',
        body: JSON.stringify(apiData),
      });

      if (response.ok) {
        const result = await response.json();
        console.log('Hub saved successfully:', result);
        
        // Close sidebar and clear selection FIRST, before refreshing
        console.log('🔄 Closing sidebar and clearing selection...');
        setShowSidebar(false);
        setSelectedHub(null);
        console.log('🔄 Sidebar should be closed now');
        
        // Show success notification with correct message
        const successMessage = isNewHub ? 'Hub created successfully!' : 'Hub updated successfully!';
        showNotification('success', successMessage, 3000);
        
        // Immediately refresh the hubs list to get the latest data
        // This ensures the new hub appears in the list right away
        try {
          const refreshResponse = await fetch(`${CONFIG.API_BASE_URL}/api/admin/hubs`, {
            method: 'GET',
            credentials: 'include',
            headers: {
              'Content-Type': 'application/json',
            },
          });
          
          if (refreshResponse.ok) {
            const refreshData = await refreshResponse.json();
            if (refreshData.success && refreshData.hubs) {
              // Transform API data to match Hub interface
              const transformedHubs: Hub[] = refreshData.hubs.map((hub: any) => ({
                id: hub.id,
                hubId: hub.hubId,
                hubName: hub.hubName,
                companyName: hub.companyName,
                country: hub.country || 'United Kingdom',
                dataCentre: hub.dataCentre || 'Unknown',
                subscriptionType: hub.subscriptionType || 'Basic',
                subscriptionStartedAt: hub.subscriptionStartedAt || '2024-01-01',
                subscriptionEndAt: hub.subscriptionEndAt || '2025-01-01',
                status: hub.status || 'active',
                superAdmin: hub.contactEmail || 'admin@company.com',
                userCount: hub.userCount || 0,
                projectCount: hub.projectCount || 0,
                hubAppUserCount: hub.hubAppUserCount || 0,
                monthlyUsage: Math.floor(Math.random() * 100) + 20,
                isConnected: true,
                realUserCount: hub.userCount || 0,
                realProjectCount: hub.projectCount || 0
              }));
              
              console.log('✅ Refreshed hubs list, count:', transformedHubs.length);
              setHubs(transformedHubs);
            }
          }
        } catch (refreshError) {
          console.error('⚠️ Failed to refresh hubs list, but hub was saved:', refreshError);
          // Don't throw error here, hub was already saved successfully
        }
        
        // 触发 Hub 更新事件，通知其他页面刷新数据
        window.dispatchEvent(new CustomEvent('hub-updated'));
        // 同时更新 localStorage 来触发跨标签页同步
        localStorage.setItem('hub-data-updated', Date.now().toString());
        console.log('✅ Hub save process completed, sidebar should be closed');
      } else {
        console.error('Failed to save hub');
        const errorText = await response.text();
        console.error('Error response:', errorText);
        showNotification('error', 'Failed to save hub. Please try again.', 5000);
        throw new Error('Failed to save hub');
      }
    } catch (error) {
      console.error('Error saving hub:', error);
      showNotification('error', 'An error occurred while saving the hub.', 5000);
      throw error;
    }
  };

  const handleDeleteHub = (hub: Hub) => {
    setHubToDelete(hub);
    setShowDeleteDialog(true);
  };

  const confirmDeleteHub = async () => {
    if (!hubToDelete) return;

    try {
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      
      const response = await fetch(getApiUrl(`/api/admin/hubs/${hubToDelete.id}`), {
        method: 'DELETE',
        headers,
        credentials: 'include',
      });

      if (response.ok) {
        // Remove the hub from local state immediately
        setHubs(prevHubs => prevHubs.filter(hub => hub.id !== hubToDelete.id));
        
        // Remove from selectedHubs if it was selected
        setSelectedHubs(prev => {
          const newSet = new Set(prev);
          newSet.delete(hubToDelete.id);
          return newSet;
        });
        
        // Clear selectedHub if it was the deleted hub
        if (selectedHub?.id === hubToDelete.id) {
          setSelectedHub(null);
          setShowSidebar(false);
        }
        
        // Show success notification
        showNotification('success', 'Hub deleted successfully!', 3000);
        
        // 触发 Hub 更新事件，通知其他页面刷新数据
        window.dispatchEvent(new CustomEvent('hub-updated'));
        // 同时更新 localStorage 来触发跨标签页同步
        localStorage.setItem('hub-data-updated', Date.now().toString());
        
        // Close dialog
        setShowDeleteDialog(false);
        setHubToDelete(null);
      } else {
        console.error('Failed to delete hub');
        const errorText = await response.text();
        console.error('Error response:', errorText);
        showNotification('error', 'Failed to delete hub. Please try again.', 5000);
        setShowDeleteDialog(false);
        setHubToDelete(null);
      }
    } catch (error) {
      console.error('Error deleting hub:', error);
      showNotification('error', 'An error occurred while deleting the hub.', 5000);
      setShowDeleteDialog(false);
      setHubToDelete(null);
    }
  };

  const handleCopyEmail = async (email: string) => {
    try {
      await navigator.clipboard.writeText(email);
      // Show copy notification
      setShowCopyNotification(true);
      setTimeout(() => setShowCopyNotification(false), 2000);
    } catch (error) {
      console.error('Failed to copy email:', error);
    }
  };

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const target = e.target as HTMLDivElement;
    setIsScrolled(target.scrollLeft > 0);
  };

  // ✅ 新增：两个滚动容器的 ref
  const topScrollRef = React.useRef<HTMLDivElement>(null);
  const bodyScrollRef = React.useRef<HTMLDivElement>(null);

  // ✅ 新增：同步函数（顶部 ⇄ 主体）
  const syncFromTop = () => {
    if (!topScrollRef.current || !bodyScrollRef.current) return;
    // 把顶部滚动条的 scrollLeft 同步给表格容器
    bodyScrollRef.current.scrollLeft = topScrollRef.current.scrollLeft;
  };

  const syncFromBody = () => {
    if (!topScrollRef.current || !bodyScrollRef.current) return;
    // 把表格容器的 scrollLeft 同步给顶部滚动条
    topScrollRef.current.scrollLeft = bodyScrollRef.current.scrollLeft;
    // 保留你原来的水平滚动阴影逻辑
    setIsScrolled(bodyScrollRef.current.scrollLeft > 0);
  };

  // ✅ 添加CSS来隐藏顶部滚动条的滚动条（Webkit浏览器）
  React.useEffect(() => {
    const style = document.createElement('style');
    style.id = 'hide-top-scrollbar';
    style.textContent = `
      .hide-top-scrollbar::-webkit-scrollbar {
        display: none;
      }
    `;
    
    // 移除已存在的样式
    const existingStyle = document.getElementById('hide-top-scrollbar');
    if (existingStyle) {
      existingStyle.remove();
    }
    
    document.head.appendChild(style);
    
    // 为顶部滚动条添加CSS类
    if (topScrollRef.current) {
      topScrollRef.current.classList.add('hide-top-scrollbar');
    }
    
    return () => {
      const styleToRemove = document.getElementById('hide-top-scrollbar');
      if (styleToRemove) {
        styleToRemove.remove();
      }
    };
  }, []);




  // Get visible columns
  const visibleColumns = columns.filter(col => col.visible);

  // Render cell content based on column key
  const renderCell = (hub: Hub, columnKey: string) => {
    switch (columnKey) {
      case 'hubName':
        return <span className="font-medium">{hub.hubName || 'N/A'}</span>;
      case 'companyName':
        return <span>{hub.companyName || 'N/A'}</span>;
      case 'country':
        return <span>{hub.country || 'N/A'}</span>;
      case 'dataCentre':
        return <span className="text-sm text-gray-600">{hub.dataCentre || 'N/A'}</span>;
      case 'subscriptionType':
        return getTypeBadge(hub.subscriptionType || 'basic');
      case 'subscriptionStartedAt':
        return <span>{hub.subscriptionStartedAt ? formatDate(hub.subscriptionStartedAt) : 'N/A'}</span>;
      case 'subscriptionEndAt':
        return <span>{hub.subscriptionEndAt ? formatDate(hub.subscriptionEndAt) : 'N/A'}</span>;
      case 'status':
        return getStatusBadge(hub.status || 'pending');
      case 'superAdmin':
        return (
          <div className="flex items-center justify-center space-x-2 group">
            <span className="text-sm text-gray-600">{hub.superAdmin || 'N/A'}</span>
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleCopyEmail(hub.superAdmin || '');
              }}
              className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 hover:bg-gray-100 p-1 rounded"
              title="Copy email"
            >
              <Copy className="w-3 h-3 text-gray-500" />
            </button>
          </div>
        );
      case 'userCount':
        return (
          <span className="font-medium text-blue-600">
            {hub.realUserCount !== undefined ? hub.realUserCount : hub.userCount}
          </span>
        );
      case 'projectCount':
        return (
          <span className="font-medium text-green-600">
            {hub.realProjectCount !== undefined ? hub.realProjectCount : hub.projectCount}
          </span>
        );
      case 'hubAppUserCount':
        return (
          <span className="font-medium text-purple-600">
            {hub.hubAppUserCount}
          </span>
        );
      case 'isConnected':
        return getConnectionBadge(hub.isConnected || false);
      default:
        return null;
    }
  };

  if (isLoading) {
    console.log('🔄 Main loading state is true, showing spinner');
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div>
      </div>
    );
  }

  return (
    <>
      {/* Top Layer Notifications - Fixed positioning, no layout impact */}
      {showCopyNotification && (
        <div className="fixed top-4 left-1/2 transform -translate-x-1/2 z-[9999] bg-gray-100 text-gray-700 px-3 py-2 rounded-md text-sm shadow-sm border border-gray-200 transition-all duration-200 ease-in-out">
          <span>Email copied to clipboard</span>
        </div>
      )}

      {notification.show && (
        <div className={`fixed top-4 right-4 z-[9999] px-4 py-3 rounded-lg shadow-xl border backdrop-blur-sm transform transition-all duration-300 ease-in-out animate-in slide-in-from-right fade-in ${
          notification.type === 'success' ? 'bg-green-500/95 text-white border-green-400' :
          notification.type === 'error' ? 'bg-red-500/95 text-white border-red-400' :
          'bg-blue-500/95 text-white border-blue-400'
        }`}>
          <div className="flex items-center space-x-2">
            {notification.type === 'success' && <CheckCircle className="w-4 h-4" />}
            {notification.type === 'error' && <XCircle className="w-4 h-4" />}
            {notification.type === 'info' && <Info className="w-4 h-4" />}
            <span className="font-medium">{notification.message}</span>
          </div>
        </div>
      )}

      <div className="space-y-6 pb-20">
      
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Hub Subscriptions</h1>
          <p className="text-gray-600">Manage all Hub subscriptions and customer information</p>
        </div>
        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-2"
          >
            <Filter className="w-4 h-4" />
            {showFilters ? 'Hide Filters' : 'Show Filters'}
          </Button>
          <ColumnConfigDropdown 
            columns={columns}
            onColumnsChange={setColumns}
          />
          <Button variant="outline" onClick={exportToCSV}>
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </Button>
          <Button onClick={(e) => handleAddHub(e)}>
            <Plus className="w-4 h-4 mr-2" />
            Add Hub
          </Button>
        </div>
      </div>

      {/* Selected Hubs Actions Bar */}
      {selectedHubs.size > 0 && (
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <span className="text-sm font-medium text-gray-700">
                  {selectedHubs.size} hub{selectedHubs.size > 1 ? 's' : ''} selected
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setSelectedHubs(new Set())}
                >
                  Clear Selection
                </Button>
              </div>
              <div className="flex items-center space-x-2">
                {selectedHubs.size === 1 ? (
                  // Single hub selected - show individual actions
                  (() => {
                    const selectedHub = paginatedHubs.find(hub => selectedHubs.has(hub.id));
                    if (!selectedHub) return null;
                    return (
                      <>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleRowClick(selectedHub)}
                        >
                          Edit Hub
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleSyncProjects(selectedHub)}
                        >
                          <RefreshCw className="w-4 h-4 mr-2" />
                          Sync Projects
                        </Button>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="outline" size="sm">
                              <MoreHorizontal className="w-4 h-4 mr-2" />
                              More
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuLabel>Actions</DropdownMenuLabel>
                            <DropdownMenuItem>View Details</DropdownMenuItem>
                            <DropdownMenuItem>Manage Users</DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem 
                              className="text-red-600"
                              onClick={() => {
                                const hub = hubs.find(h => h.id === selectedHub.id);
                                if (hub) handleDeleteHub(hub);
                              }}
                            >
                              Delete Hub
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </>
                    );
                  })()
                ) : (
                  // Multiple hubs selected - show bulk actions
                  <>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        // Sync projects for all selected hubs
                        const selectedHubObjects = paginatedHubs.filter(hub => selectedHubs.has(hub.id));
                        selectedHubObjects.forEach(hub => {
                          handleSyncProjects(hub);
                        });
                      }}
                    >
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Sync Projects ({selectedHubs.size})
                    </Button>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="outline" size="sm">
                          <MoreHorizontal className="w-4 h-4 mr-2" />
                          More
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuLabel>Bulk Actions</DropdownMenuLabel>
                        <DropdownMenuItem disabled>
                          More bulk actions coming soon
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}



      {/* Filters */}
      {showFilters && (
        <Card>
          <CardContent className="p-6">
            <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <Input
                  placeholder="Search hubs, companies, or admins..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-48">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="expired">Expired</SelectItem>
                <SelectItem value="suspended">Suspended</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
              </SelectContent>
            </Select>
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-full sm:w-48">
                <SelectValue placeholder="Filter by type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="Free Trial">Free Trial</SelectItem>
                <SelectItem value="Basic">Basic</SelectItem>
                <SelectItem value="Enterprise Basic">Enterprise Basic</SelectItem>
                <SelectItem value="Enterprise Premium">Enterprise Premium</SelectItem>
              </SelectContent>
            </Select>
            <Select value={countryFilter} onValueChange={setCountryFilter}>
              <SelectTrigger className="w-full sm:w-48">
                <SelectValue placeholder="Filter by country" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Countries</SelectItem>
                <SelectItem value="uk">United Kingdom</SelectItem>
                <SelectItem value="us">United States</SelectItem>
                <SelectItem value="ca">Canada</SelectItem>
                <SelectItem value="au">Australia</SelectItem>
                <SelectItem value="de">Germany</SelectItem>
                <SelectItem value="fr">France</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>
      )}

      {/* Table */}
      <Card className="overflow-hidden" style={{marginBottom: '8px'}}>
        <CardContent className="p-0">

          {/* ✅ 新增：顶部"镜像滚动条"——完全隐藏但保持滚动功能 */}
          <div
            ref={topScrollRef}
            onScroll={syncFromTop}
            className="w-full overflow-x-auto hide-top-scrollbar"
            style={{ 
              height: 0,
              scrollbarGutter: 'stable',
              scrollbarWidth: 'none',
              msOverflowStyle: 'none'
            }}
          >
            <div style={{ width: '1400px', height: 1 }} />
          </div>

          {/* ✅ 修改：主滚动容器 */}
          <div
            ref={bodyScrollRef}
            className="overflow-x-auto overflow-y-scroll"
            style={{
              height: 'calc(100vh - 280px)',
              maxWidth: '100%',
            }}
            onScroll={syncFromBody}
          >
            {/* 表格本体 */}
            <Table className="border-collapse" style={{ minWidth: '1400px', width: '1400px' }}>
              {/* ❗️注意：这里不要再给 TableHeader 外层加 sticky，
                   让每个 <TableHead/>（th）自己 sticky（我们在组件里已经改好了）
              */}
              <TableHeader>
                <TableRow className="bg-gradient-to-r from-gray-50 to-gray-100 border-b-2 border-gray-200">
                  {/* Checkbox column */}
                  <TableHead
                    className="py-4 px-3 sticky left-0 text-center"
                    style={{ 
                      backgroundColor: '#f9fafb', 
                      opacity: 1, 
                      width: '48px', 
                      minWidth: '48px', 
                      maxWidth: '48px', 
                      zIndex: 100,
                      position: 'sticky'
                    }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <input
                      type="checkbox"
                      checked={paginatedHubs.length > 0 && paginatedHubs.every(hub => selectedHubs.has(hub.id))}
                      onChange={(e) => handleSelectAll(e.target.checked)}
                      className="w-4 h-4 text-red-600 rounded focus:ring-red-500"
                      onClick={(e) => e.stopPropagation()}
                    />
                  </TableHead>
                  {visibleColumns.map((column) => (
                    <TableHead
                      key={column.key}
                      // 如果是冻结列，继续设置 sticky left
                      className={`py-4 px-3 font-semibold text-gray-700 uppercase tracking-wide text-xs cursor-pointer hover:bg-gray-200 transition-colors duration-200 select-none ${column.width} text-center${column.frozen ? ' sticky shadow-lg' : ''}${column.frozen && isScrolled ? ' border-r border-gray-300' : ''}`}
                      style={column.frozen ? {
                        backgroundColor: '#f9fafb',
                        opacity: 1,
                        left: '48px',
                        zIndex: 99,
                        position: 'sticky'
                      } : {}}
                      onClick={() => column.sortable && handleSort(column.key as keyof Hub)}
                    >
                      <div className="flex items-center justify-center space-x-2">
                        <span className="font-medium">{column.label}</span>
                        {column.sortable && getSortIcon(column.key as keyof Hub)}
                      </div>
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginatedHubs.map((hub) => {
                  const isSelected = selectedHubs.has(hub.id);
                  return (
                  <TableRow
                    key={hub.id}
                      className={`transition-colors border-b border-gray-100 ${isSelected ? 'bg-blue-50' : ''}`}
                      onMouseEnter={(e) => {
                        // Don't apply hover to frozen columns
                        const row = e.currentTarget;
                        const frozenCells = row.querySelectorAll('[style*="position: sticky"]');
                        frozenCells.forEach((cell: any) => {
                          if (cell.style.position === 'sticky') {
                            const isSelected = selectedHubs.has(hub.id);
                            cell.style.backgroundColor = isSelected ? '#dbeafe' : '#ffffff';
                          }
                        });
                      }}
                      onClick={(e) => {
                        // Only handle row click if not clicking on interactive elements
                        const target = e.target as HTMLElement;
                        if (target.tagName === 'INPUT' || target.closest('button') || target.closest('[role="button"]')) {
                          return;
                        }
                        handleRowClick(hub);
                      }}
                    >
                      {/* Checkbox cell */}
                      <TableCell
                        className="py-3 px-3 sticky left-0 text-center"
                        style={{ 
                          backgroundColor: isSelected ? '#dbeafe' : '#ffffff', 
                          opacity: 1, 
                          width: '48px', 
                          minWidth: '48px', 
                          maxWidth: '48px', 
                          zIndex: 100,
                          position: 'sticky'
                        }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={(e) => {
                            e.stopPropagation();
                            handleCheckboxChange(hub.id, e.target.checked);
                          }}
                          className="w-4 h-4 text-red-600 rounded focus:ring-red-500"
                          onClick={(e) => e.stopPropagation()}
                        />
                      </TableCell>
                    {visibleColumns.map((column) => (
                      <TableCell
                        key={column.key}
                          className={`py-3 px-3 ${column.width} text-center transition-colors cursor-pointer${column.frozen ? ' sticky shadow-lg' : ' hover:bg-gray-50'}${column.frozen && isScrolled ? ' border-r border-gray-300' : ''}`}
                          style={column.frozen ? {
                            backgroundColor: isSelected ? '#dbeafe' : '#ffffff',
                            opacity: 1,
                            left: '48px',
                            zIndex: 99,
                            position: 'sticky',
                            pointerEvents: 'auto'
                          } : {}}
                          onMouseEnter={(e) => {
                            // Prevent hover effect on frozen columns
                            if (column.frozen) {
                              e.currentTarget.style.backgroundColor = isSelected ? '#dbeafe' : '#ffffff';
                            }
                          }}
                          onClick={() => handleRowClick(hub)}
                      >
                        {renderCell(hub, column.key)}
                      </TableCell>
                    ))}
                  </TableRow>
                  );
                })}

                {/* 保留你的空行逻辑 */}
                {Array.from({ length: Math.max(0, 15 - paginatedHubs.length) }).map((_, index) => (
                  <TableRow key={`empty-${index}`} className="border-b border-gray-100">
                    <TableCell 
                      className="py-3 px-3 sticky left-0 text-center"
                      style={{ 
                        backgroundColor: '#ffffff', 
                        opacity: 1, 
                        width: '48px', 
                        minWidth: '48px', 
                        maxWidth: '48px', 
                        zIndex: 100,
                        position: 'sticky'
                      }}
                    >
                      &nbsp;
                    </TableCell>
                    {visibleColumns.map((column) => (
                      <TableCell
                        key={column.key}
                        className={`py-3 px-3 ${column.width} text-center${column.frozen ? ' sticky' : ''}`}
                        style={column.frozen ? {
                          backgroundColor: '#ffffff',
                          opacity: 1,
                          left: '48px',
                          zIndex: 99,
                          position: 'sticky'
                        } : {}}
                      >
                        &nbsp;
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {/* ✅ 分页区域保持在滚动条下面（也就是"滚动条在分页上方"） */}
          <div className="bg-white">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={totalItems}
              itemsPerPage={itemsPerPage}
              onPageChange={handlePageChange}
            />
          </div>
        </CardContent>
      </Card>
      


      {/* Right Sidebar for Hub Details */}
      {showSidebar && selectedHub && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50">
          <div className="absolute right-0 top-0 h-full w-full max-w-md bg-white shadow-xl transform transition-transform duration-300 ease-in-out">
            <div className="h-full flex flex-col">
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                    <Building2 className="w-6 h-6 text-purple-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      {selectedHub.id ? (selectedHub.hubName || 'Unnamed Hub') : 'Add New Hub'}
                    </h3>
                    <p className="text-sm text-gray-500">
                      {selectedHub.id ? (
                        selectedHub.hubId ? (
                          `Hub ID: ${selectedHub.hubId}`
                        ) : (
                          'Edit hub details'
                        )
                      ) : (
                        'Create new hub'
                      )}
                    </p>
                  </div>
                </div>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => {
                    setShowSidebar(false);
                    setSelectedHub(null);
                  }}
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto p-6">
                <HubSidebarForm 
                  hub={selectedHub}
                  onSave={handleSaveHub}
                  onCancel={() => {
                    setShowSidebar(false);
                    setSelectedHub(null);
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent className="sm:max-w-lg">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              Delete Hub
            </AlertDialogTitle>
            <AlertDialogDescription className="text-gray-600 pt-2">
              Are you sure you want to delete this hub? This action will permanently delete the hub from the Neon database and cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          
          {hubToDelete && (
            <div className="py-4">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 space-y-3">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
                  <div className="flex-1 space-y-2">
                    <div>
                      <span className="font-semibold text-gray-900">Hub Name:</span>
                      <span className="ml-2 text-gray-700">{hubToDelete.hubName || 'N/A'}</span>
                    </div>
                    <div>
                      <span className="font-semibold text-gray-900">Company:</span>
                      <span className="ml-2 text-gray-700">{hubToDelete.companyName || 'N/A'}</span>
                    </div>
                    {hubToDelete.hubId && (
                      <div>
                        <span className="font-semibold text-gray-900">Hub ID:</span>
                        <span className="ml-2 text-gray-700 font-mono text-sm">{hubToDelete.hubId}</span>
                      </div>
                    )}
                    <div className="pt-2 border-t border-red-200">
                      <p className="text-sm text-red-800 font-medium">
                        ⚠️ This will permanently delete the hub record from the Neon database.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => {
              setShowDeleteDialog(false);
              setHubToDelete(null);
            }}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDeleteHub}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete Hub
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      </div>
    </>
  );
};

// Hub Sidebar Form Component
interface HubSidebarFormProps {
  hub: Hub;
  onSave: (data: Partial<Hub>) => Promise<void>;
  onCancel: () => void;
}

const HubSidebarForm: React.FC<HubSidebarFormProps> = ({ hub, onSave, onCancel }) => {
  const [isSaving, setIsSaving] = useState(false);
  
  // Debug: Monitor isSaving state changes
  React.useEffect(() => {
    console.log('🔄 isSaving state changed to:', isSaving);
  }, [isSaving]);
  const [formData, setFormData] = useState({
    hubId: hub.hubId || '', // Only use ACC Hub ID, never fallback to database UUID
    hubName: hub.hubName || '',
    companyName: hub.companyName || '',
    country: hub.country || 'United Kingdom',
    dataCentre: hub.dataCentre || '',
    subscriptionType: hub.subscriptionType || 'Basic',
    status: hub.status || 'active',
    superAdmin: hub.superAdmin || '',
    userCount: hub.userCount || 0,
    projectCount: hub.projectCount || 0,
    realUserCount: hub.realUserCount || hub.userCount || 0,
    realProjectCount: hub.realProjectCount || hub.projectCount || 0,
    subscriptionStartedAt: hub.subscriptionStartedAt || new Date().toISOString().split('T')[0],
    subscriptionEndAt: hub.subscriptionEndAt || '',
    isConnected: hub.isConnected || false,
    // Additional fields for API compatibility
    contactEmail: hub.superAdmin || '',
    contactPhone: '',
    address: '',
    industry: '',
    employeeCount: 0,
    monthlyFee: 0,
    annualFee: 0,
    paymentMethod: '',
    notes: ''
  });

  const [dateError, setDateError] = useState<string>('');

  const validateDates = () => {
    if (formData.subscriptionStartedAt && formData.subscriptionEndAt) {
      const startDate = new Date(formData.subscriptionStartedAt);
      const endDate = new Date(formData.subscriptionEndAt);
      
      if (endDate < startDate) {
        setDateError('End date cannot be earlier than start date');
        return false;
      }
    }
    setDateError('');
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.hubName || !formData.hubName.trim()) {
      alert('Please enter a Hub Name');
      return;
    }
    
    if (!formData.companyName || !formData.companyName.trim()) {
      alert('Please enter a Company Name');
      return;
    }
    
    if (!formData.country || !formData.country.trim()) {
      alert('Please select a Country');
      return;
    }
    
    // Validate dates before submitting
    if (!validateDates()) {
      return;
    }
    
    console.log('🔄 Starting save process...');
    console.log('🔄 Form data being submitted:', formData);
    console.log('🔄 Setting isSaving to true');
    setIsSaving(true);
    try {
      await onSave(formData);
      console.log('✅ Save process completed successfully');
    } catch (error) {
      console.error('❌ Save process failed:', error);
      throw error;
    } finally {
      console.log('🔄 Resetting saving state...');
      console.log('🔄 Setting isSaving to false');
      setIsSaving(false);
      console.log('🔄 isSaving state should now be false');
    }
  };

  return (
    <div className="space-y-6">
      {/* Hub Info Section */}
      <div className="space-y-4">
        <h4 className="font-medium text-gray-900">Hub Information</h4>
        
        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Hub ID <span className="text-xs text-gray-500">(ACC Hub ID)</span> <span className="text-red-500">*</span>
            </label>
            <Input
              value={formData.hubId}
              onChange={(e) => setFormData({ ...formData, hubId: e.target.value })}
              placeholder="e.g., b.aa65956f-280b-47d2-9e2d-f1d340dee387"
              required
            />
            <p className="text-xs text-gray-500 mt-1">The unique ACC Hub ID from Autodesk</p>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Hub Name <span className="text-xs text-gray-500">(Auto from ACC API)</span> <span className="text-red-500">*</span>
            </label>
            <Input
              value={formData.hubName}
              onChange={(e) => setFormData({ ...formData, hubName: e.target.value })}
              placeholder="Enter hub name"
              required
              readOnly={!!hub.id && hub.id !== ''} // Only read-only when editing existing hub
              className={hub.id && hub.id !== '' ? "bg-gray-50 cursor-not-allowed" : ""}
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Company Name <span className="text-red-500">*</span>
            </label>
            <Input
              value={formData.companyName}
              onChange={(e) => setFormData({ ...formData, companyName: e.target.value })}
              placeholder="Enter company name"
              required
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Country <span className="text-red-500">*</span>
            </label>
            <Select 
              value={formData.country} 
              onValueChange={(value) => setFormData({ ...formData, country: value })}
              required
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="United Kingdom">United Kingdom</SelectItem>
                <SelectItem value="United States">United States</SelectItem>
                <SelectItem value="Canada">Canada</SelectItem>
                <SelectItem value="Australia">Australia</SelectItem>
                <SelectItem value="Germany">Germany</SelectItem>
                <SelectItem value="France">France</SelectItem>
                <SelectItem value="Japan">Japan</SelectItem>
                <SelectItem value="China">China</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Data Centre <span className="text-xs text-gray-500">(Auto from ACC API)</span>
            </label>
            <Input
              value={formData.dataCentre}
              readOnly
              className="bg-gray-50 cursor-not-allowed"
            />
          </div>
        </div>
      </div>

      {/* Subscription Details */}
      <div className="space-y-4">
        <h4 className="font-medium text-gray-900">Subscription Details</h4>
        
        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Subscription Type
            </label>
            <Select 
              value={formData.subscriptionType} 
              onValueChange={(value) => setFormData({ ...formData, subscriptionType: value as any })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Free Trial">Free Trial</SelectItem>
                <SelectItem value="Basic">Basic</SelectItem>
                <SelectItem value="Enterprise Basic">Enterprise Basic</SelectItem>
                <SelectItem value="Enterprise Premium">Enterprise Premium</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Status
            </label>
            <Select 
              value={formData.status} 
              onValueChange={(value) => setFormData({ ...formData, status: value as any })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="expired">Expired</SelectItem>
                <SelectItem value="suspended">Suspended</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Start At
              </label>
              <Input
                type="date"
                value={formData.subscriptionStartedAt}
                onChange={(e) => {
                  setFormData({ ...formData, subscriptionStartedAt: e.target.value });
                  setDateError(''); // Clear error when user changes date
                }}
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                End At
              </label>
              <Input
                type="date"
                value={formData.subscriptionEndAt}
                onChange={(e) => {
                  setFormData({ ...formData, subscriptionEndAt: e.target.value });
                  setDateError(''); // Clear error when user changes date
                }}
              />
            </div>
          </div>
          
          {/* Date validation error message */}
          {dateError && (
            <div className="text-red-600 text-sm mt-2 flex items-center gap-2">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              {dateError}
            </div>
          )}
        </div>
      </div>

      {/* Admin & Stats */}
      <div className="space-y-4">
        <h4 className="font-medium text-gray-900">Administration & Statistics</h4>
        
        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Super Admin Email
            </label>
            <Input
              type="email"
              value={formData.superAdmin}
              onChange={(e) => setFormData({ ...formData, superAdmin: e.target.value })}
              required
            />
          </div>
          
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                User Count (Real-time)
              </label>
              <div className="px-3 py-2 bg-gray-50 border border-gray-300 rounded-md text-sm text-gray-900 font-medium">
                {formData.realUserCount !== undefined ? formData.realUserCount : formData.userCount} users
              </div>
              <p className="text-xs text-gray-500 mt-1">Auto-calculated from database</p>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Project Count (Real-time)
              </label>
              <div className="px-3 py-2 bg-gray-50 border border-gray-300 rounded-md text-sm text-gray-900 font-medium">
                {formData.realProjectCount !== undefined ? formData.realProjectCount : formData.projectCount} projects
              </div>
              <p className="text-xs text-gray-500 mt-1">Auto-calculated from database</p>
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="isConnected"
              checked={formData.isConnected}
              onChange={(e) => setFormData({ ...formData, isConnected: e.target.checked })}
              className="rounded border-gray-300"
            />
            <label htmlFor="isConnected" className="text-sm font-medium text-gray-700">
              Connected to our application
            </label>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex justify-end space-x-2 pt-4 border-t">
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} disabled={isSaving}>
          {isSaving ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              {hub.id ? 'Updating...' : 'Creating...'}
            </>
          ) : (
            hub.id ? 'Update Hub' : 'Create Hub'
          )}
        </Button>
      </div>
    </div>
  );
};

export default AdminHubs;
